import React, {useRef} from 'react';
import {StyleSheet, View, FlatList, Platform} from 'react-native';
import {
  CommonActions,
  ParamListBase,
  TabActionHelpers,
  TabNavigationState,
  TabRouterOptions,
  useNavigationBuilder,
} from '@react-navigation/native';
import LinearGradient, {
  type LinearGradientProps,
} from 'react-native-linear-gradient';
import {createMaterialTopTabNavigator} from '@react-navigation/material-top-tabs';
import Styles from 'src/components/Styles';
import AVTouchableOpacity from 'src/components/elements/AVTouchableOpacity';
import {getDescriber} from 'src/components/elements/descriptor/DescriptorType';
import AVText from 'src/components/elements/AVText';
import {getButtonDescriber} from 'src/components/elements/buttonDescriptor/DescriptorType';
import Settings from 'src/Settings';

// Supported screen options
interface TabNavigationOptions {
  title?: string;
}

// Map of event name and the type of data (in event.data)
//
// canPreventDefault: true adds the defaultPrevented property to the
// emitted events.
interface TabNavigationEventMap {
  tabPress: {
    data: {isAlreadyFocused: boolean};
    canPreventDefault: true;
  };
}

export const useRewardsNavigation = useNavigationBuilder<
  TabNavigationState<ParamListBase>,
  TabRouterOptions,
  TabActionHelpers<ParamListBase>,
  TabNavigationOptions,
  TabNavigationEventMap
>;

// FIXME: workaround for the issue with the Screen component is not exported
export const TabScreen = createMaterialTopTabNavigator().Screen;

type RewardsTabBarProps = Omit<
  ReturnType<typeof useRewardsNavigation>,
  'NavigationContent'
>;

export default function RewardsTabBar({
  state,
  descriptors,
  navigation,
}: RewardsTabBarProps) {
  const headerList = useRef(null);
  const rawColors = getDescriber()?.checkRewardsBackgroundColor?.() ?? [];
  const colors = Array.isArray(rawColors) ? rawColors.filter(Boolean) : [];
  const safeColors =
    colors.length >= 3
      ? colors.slice(0, 3)
      : colors.length === 2
      ? colors
      : [Styles.tabBarBackgroundColor, Styles.tabBarBackgroundColor];

  const locations =
    safeColors.length === 3
      ? [0, 0.9, 1]
      : safeColors.length === 2
      ? [0, 1]
      : undefined;

  const activeColor =
    getButtonDescriber()?.checkRoundedBtnColor?.() ??
    Styles.Colors?.Canteen?.darkGreen ??
    '#007AFF';

  const gradientProps: LinearGradientProps = {
    start: {x: 0, y: 0},
    end: {x: 0, y: 1},
    colors: safeColors,
    ...(locations ? {locations} : {}),
  };

  const renderItem = ({item, index}) => {
    const route = item;
    const isFocused = state.index === index;
    const label = descriptors[route.key].options.title || String(route.name);

    const handlePress = () => {
      const event = navigation.emit({
        type: 'tabPress',
        target: route.key,
        canPreventDefault: true,
        data: {isAlreadyFocused: isFocused},
      });

      if (!isFocused && !event?.defaultPrevented) {
        navigation.dispatch({
          ...CommonActions.navigate(route),
          target: state.key,
        });
      }
      headerList?.current?.scrollToIndex({
        index,
        animated: true,
        viewPosition: 1,
      });
    };
    return (
      <AVTouchableOpacity
        key={route.key}
        accessible
        accessibilityHint={label}
        accessibilityRole="button"
        role="button"
        accessibilityLabel={label}
        aria-label={label}
        testID={`qa_btn_${label}`}
        onPress={handlePress}
        style={[
          tabStyles.tabBarItemStyle,
          {
            marginRight: index === 2 ? Styles.Spacing.m3 : 0,
            marginLeft: index === 0 ? Styles.Spacing.m3 : Styles.Spacing.m2,
          },
        ]}
      >
        <View
          style={[
            tabStyles.tabBarLabel,
            isFocused && {
              borderColor: activeColor,
              backgroundColor: activeColor,
            },
          ]}
          nativeID={`qa_btn_${label}`}
        >
          <AVText
            style={[
              tabStyles.labelText,
              !!isFocused && tabStyles.labelTextFocused,
              !!isFocused &&
                Settings.buildType === 'companykitchen' &&
                tabStyles.ckColor,
            ]}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
          >
            {label}
          </AVText>
        </View>
      </AVTouchableOpacity>
    );
  };

  return (
    <LinearGradient {...gradientProps} style={tabStyles.tabBarStyle}>
      <FlatList
        horizontal
        data={state.routes}
        renderItem={renderItem}
        showsHorizontalScrollIndicator={false}
        keyExtractor={(item) => `${item.key} ${item.name}`}
        style={tabStyles.tabsList}
        ref={headerList}
        onScrollToIndexFailed={(error) => {
          headerList.current?.scrollToOffset({
            offset: error?.index * error?.averageItemLength,
            animated: true,
          });
        }}
      />
    </LinearGradient>
  );
}

const tabStyles = StyleSheet.create({
  tabBarStyle: {
    backgroundColor: 'transparent',
    flexDirection: 'row',
    paddingVertical: Styles.Spacing.m3,
    paddingHorizontal: Styles.Spacing.m3,
    gap: Styles.Spacing.m2,
  },
  tabBarItemStyle: {
    width: 'auto',
    padding: 0,
    height: Styles.Heights.touchTargetHeight2,
    justifyContent: 'center',
  },
  tabBarLabel: {
    borderColor: Styles.neutralHues,
    borderWidth: 1,
    borderRadius: Styles.Spacing.m4,
    paddingVertical: Styles.Spacing.m2,
    paddingHorizontal: Styles.Spacing.m3,
    backgroundColor: Styles.white,
    elevation: 2,
    shadowColor: Styles.black,
    shadowOffset: {width: 4, height: 4},
    shadowOpacity: 0.04,
    shadowRadius: 4,
    overflow: 'hidden',
    height: Styles.Heights.touchTargetHeight1,
  },
  tabBarLabelFocused: {
    borderColor: Styles.Colors.PayNew.primary01,
    backgroundColor: Styles.Colors.PayNew.primary01,
  },
  labelText: {
    fontSize: 18,
    fontWeight: '400',
    color: Styles.tabBarTextColor,
    paddingHorizontal: Styles.Spacing.m1,
    height: Styles.Heights.touchTargetHeight1,
    paddingTop: 2,
  },
  labelTextFocused: {
    fontWeight: '700',
    color: Styles.white,
    paddingHorizontal: 0,
    ...Platform.select({
      android: {
        paddingTop: Styles.Spacing.m0,
      },
    }),
  },
  ckColor: {color: 'black'},
  tabsList: {
    marginHorizontal: -Styles.Spacing.m3,
  },
});
